/**
 * LocalStorage-backed outbox for the LexIDE activity log.
 *
 * Mirror of [`useFeedbackStore`](./useFeedbackStore.ts) — same
 * Zustand + `persist` middleware pattern, same FIFO eviction at the
 * cap. Distinct store name so feedback + activity quotas don't
 * crowd each other.
 *
 * Activity emits are higher-rate than feedback (every nav, click,
 * etc.), so the cap is 500 — past 500 unflushed events the oldest
 * are dropped. The flusher (`<ActivityFlusher>`) drains the queue
 * on a 5 s debounce / 100-event threshold / `pagehide`.
 *
 * See `ai-docs/user-activity-logging-implementation-plan.md` (L7).
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

import type { ActivityEvent } from "@lexiun/lx-obs";

const MAX_QUEUE_SIZE = 500;

interface ActivityStoreState {
  outbox: ActivityEvent[];
  isFlushing: boolean;

  enqueue: (event: ActivityEvent) => void;
  enqueueMany: (events: ActivityEvent[]) => void;
  /** Remove successfully-sent events by id. */
  ack: (ids: ReadonlyArray<string>) => void;
  setFlushing: (flushing: boolean) => void;
  /** Drop everything (debug + sign-out cleanup). */
  clear: () => void;
}

export const useActivityStore = create<ActivityStoreState>()(
  persist(
    (set) => ({
      outbox: [],
      isFlushing: false,

      enqueue: (event) =>
        set((s) => ({ outbox: appendBounded(s.outbox, [event]) })),

      enqueueMany: (events) =>
        set((s) => ({ outbox: appendBounded(s.outbox, events) })),

      ack: (ids) =>
        set((s) => {
          const set = new Set(ids);
          return { outbox: s.outbox.filter((e) => !set.has(e.id)) };
        }),

      setFlushing: (flushing) => set({ isFlushing: flushing }),
      clear: () => set({ outbox: [], isFlushing: false }),
    }),
    {
      name: "lexide-activity-outbox",
      storage: createJSONStorage(() => localStorage),
      // Don't persist isFlushing — it's runtime only.
      partialize: (s) => ({ outbox: s.outbox }),
    },
  ),
);

function appendBounded(
  existing: ActivityEvent[],
  incoming: ActivityEvent[],
): ActivityEvent[] {
  if (incoming.length === 0) return existing;
  const seen = new Set(existing.map((e) => e.id));
  const deduped = incoming.filter((e) => !seen.has(e.id));
  if (deduped.length === 0) return existing;
  const combined = existing.concat(deduped);
  if (combined.length <= MAX_QUEUE_SIZE) return combined;
  // FIFO eviction: drop oldest.
  return combined.slice(combined.length - MAX_QUEUE_SIZE);
}
